import {assert} from 'Asserts';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  CustomStructuredTableColumn,
  EvalTableView,
  GetCustomEvalArgsOptions,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableColumnGroups,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {
  CustomEvalFnAndType,
  CustomEvalResult,
  CustomEvalType,
} from 'notebook/tables/constants/CustomEvalConstants';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {TABLE_SMALL_COLUMN_WIDTH} from 'notebook/tables/constants/TableLayoutConstants';
import {CellIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {toSpreadsheetBooleanValue, ValueError} from 'notebook/tables/excess/Util';
import {RowScopedUnitTypeFilterType} from 'notebook/tables/search-scoping/rowScopedUnitTypeFilter';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {coerceToPrimitiveBoolean, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {
  libraryParameterDescriptionCustomEval,
  libraryParameterUnitCustomEval,
} from 'procedures/parameter-library/libraryParameterColumnCustomEvals';
import {getLibraryDefinitionTypedValue} from 'procedures/parameter-library/libraryParameterDefinitionTypedValueUtils';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {Optional} from 'util/Types';

/**
 * Reaction Conditions is a chemistry sub-table (rendered inside the Stoichiometry note-linked
 * group) that captures the condition values used for a Reaction — e.g. temperature, RPM, pressure.
 *
 * Columns:
 *  - Parameter: chosen from the shared Parameter Library
 *    ({@link TableColumnType.PROCEDURE_LIBRARY_PARAMETER_SELECTOR}). The cell stores a
 *    `LIBRARY_PARAMETER_DEFINITION` typed value carrying the definition's id + name.
 *  - Unit: a {@link TableColumnType.UNIT_REFERENCE} column whose picker is narrowed to the Parameter's unit type and
 *    whose value is validated against it.
 *  - Value: an editable float — the recorded condition value.
 *  - Description: read-only, derived from the chosen library parameter definition.
 *  - Comment: an editable free-text note.
 *
 * A row is blank when Parameter, Unit and Value are all empty (Comment is ignored), and a blank row is valid so a
 * table can be submitted empty. Once any of those three is filled, all three are required, the Parameter must be a
 * parameter a Reaction Condition can record, and the Unit must belong to the Parameter's unit type. Parameter, Unit
 * and Value cannot reference each other's evaluated cells (that would be a circular dependency), so each eval is
 * given a literal flag for whether the row's other required cells are filled.
 *
 * Creation is gated behind REACTION_PARAMETERS_ENABLED.
 */
export interface ReactionConditionsStructuredTableMetadata {}

export interface ReactionConditionsStructuredTableRowMetadata extends RowMetadata {}

export type ReactionConditionsStructuredTable = StructuredTable<
  ReactionConditionsStructuredTableMetadata,
  ReactionConditionsStructuredTableRowMetadata
>;

const REACTION_CONDITION_COLUMN_GROUPS: ReadonlySet<StructuredTableColumnGroups> = new Set([
  StructuredTableColumnGroups.REACTION_CONDITION_PARAMETER,
  StructuredTableColumnGroups.REACTION_CONDITION_UNIT,
  StructuredTableColumnGroups.REACTION_CONDITION_VALUE,
  StructuredTableColumnGroups.REACTION_CONDITION_DESCRIPTION,
  StructuredTableColumnGroups.REACTION_CONDITION_COMMENT,
]);

/** Whether the user has entered anything in the cell, by the same definition of empty the evals apply to values. */
function isCellFilled(cell: Cell): boolean {
  return (cell.typedValue != null && !isTypedValueEmpty(cell.typedValue)) || cell.text.trim() !== '';
}

function requiredCellResult(
  typedValue: TypedValue,
  columnName: string,
  isRequired: boolean
): CustomEvalResult {
  return {
    val: typedValue,
    error: isRequired ? new ValueError(`${columnName} is required`) : null,
    locked: false,
  };
}

/**
 * Validates the Parameter. `cellDependencies[0]` is a literal flag for whether the row's Unit or Value is filled,
 * which makes an empty Parameter an error. A chosen Parameter must be one a Reaction Condition can record.
 */
function parameterColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: {
    tableMetadata: ReactionConditionsStructuredTableMetadata;
    rowMetadata: ReactionConditionsStructuredTableRowMetadata;
  },
  resolveFromResolutionQuery: ResolveQueryFn,
  resolveTypedValueToType: ResolveTypedValueToTypeFn,
  _fileId?: string | null
): CustomEvalResult {
  if (isTypedValueEmpty(typedValue)) {
    return requiredCellResult(
      typedValue,
      'Parameter',
      coerceToPrimitiveBoolean(cellDependencies[0]) ?? false
    );
  }

  // Custom evals run before typed value resolution, so text or formula input hasn't been resolved to a library
  // definition yet.
  let parameterTypedValue = typedValue;
  if (columnDefinition != null) {
    const {resolvedValue, error: resolutionError} = resolveTypedValueToType(columnDefinition, typedValue);
    if (resolutionError != null) {
      return {val: resolvedValue ?? typedValue, error: resolutionError, locked: false};
    }
    parameterTypedValue = resolvedValue ?? typedValue;
  }

  const libraryDefinitionTypedValue = getLibraryDefinitionTypedValue(parameterTypedValue);
  if (libraryDefinitionTypedValue == null) {
    return {val: parameterTypedValue, error: null, locked: false};
  }

  const {resolvedValue, error} = resolveFromResolutionQuery(
    parameterTypedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.REACTION_CONDITION_VALIDATE_PARAMETER,
      libraryParameterDefinitionId: libraryDefinitionTypedValue.id,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue ?? parameterTypedValue, error, locked: false};
}

/**
 * Validates the Value. `cellDependencies[0]` is a literal flag for whether the row's Parameter or Unit is filled,
 * which makes an empty Value an error. Any number is accepted; the column's float type handles non-numeric input.
 */
function valueColumnCustomEval(
  typedValue: TypedValue,
  _columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: {
    tableMetadata: ReactionConditionsStructuredTableMetadata;
    rowMetadata: ReactionConditionsStructuredTableRowMetadata;
  },
  _resolveFromResolutionQuery: ResolveQueryFn,
  _resolveTypedValueToType: ResolveTypedValueToTypeFn,
  _fileId?: string | null
): CustomEvalResult {
  return requiredCellResult(
    typedValue,
    'Value',
    isTypedValueEmpty(typedValue) && (coerceToPrimitiveBoolean(cellDependencies[0]) ?? false)
  );
}

/**
 * Validates the Unit against the unit type of the chosen library parameter. `cellDependencies[0]` is the Parameter
 * column's typed value and `cellDependencies[1]` is a literal flag for whether the row's Value is filled. Validation
 * against the parameter's unit type is shared with other Parameter Library tables.
 */
function unitColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: {
    tableMetadata: ReactionConditionsStructuredTableMetadata;
    rowMetadata: ReactionConditionsStructuredTableRowMetadata;
  },
  resolveFromResolutionQuery: ResolveQueryFn,
  resolveTypedValueToType: ResolveTypedValueToTypeFn,
  fileId?: string | null
): CustomEvalResult {
  const libraryDefinitionTypedValue = getLibraryDefinitionTypedValue(cellDependencies[0]);
  // If no library parameter is selected yet, there is no unit type to validate against. The Unit is still required
  // once the row's Value is filled.
  if (libraryDefinitionTypedValue == null) {
    return requiredCellResult(
      typedValue,
      'Unit',
      isTypedValueEmpty(typedValue) && (coerceToPrimitiveBoolean(cellDependencies[1]) ?? false)
    );
  }

  return libraryParameterUnitCustomEval(
    typedValue,
    columnDefinition,
    cellDependencies,
    customEvalOptions,
    resolveFromResolutionQuery,
    resolveTypedValueToType,
    fileId
  );
}

export default class ReactionConditionsStructuredTableAdapter extends GenericStructuredTableAdapter<
  ReactionConditionsStructuredTableMetadata,
  ReactionConditionsStructuredTableRowMetadata
> {
  override getDisplayName(_structuredTable: ReactionConditionsStructuredTable): string {
    return 'Reaction Conditions';
  }

  override shouldSuppressTitleBar(_table: ReactionConditionsStructuredTable): boolean {
    return true;
  }

  override shouldShowFormulaBox(_table: ReactionConditionsStructuredTable): boolean {
    return true;
  }

  override getBlankRowMetadata(
    _tableMetadata: ReactionConditionsStructuredTableMetadata
  ): ReactionConditionsStructuredTableRowMetadata {
    return {};
  }

  override _getAllSupportedColumns(_params: {
    entryId: string | null;
    metadata: ReactionConditionsStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return [
      makeStructuredTableColumn({
        columnType: TableColumnType.PROCEDURE_LIBRARY_PARAMETER_SELECTOR,
        name: 'Parameter',
        columnGroup: StructuredTableColumnGroups.REACTION_CONDITION_PARAMETER,
      }),
      makeStructuredTableColumn({
        columnType: TableColumnType.UNIT_REFERENCE,
        name: 'Unit',
        columnGroup: StructuredTableColumnGroups.REACTION_CONDITION_UNIT,
        width: TABLE_SMALL_COLUMN_WIDTH,
      }),
      {
        ...makeStructuredTableColumn({
          columnType: TableColumnType.CUSTOM,
          name: 'Value',
          columnGroup: StructuredTableColumnGroups.REACTION_CONDITION_VALUE,
          width: TABLE_SMALL_COLUMN_WIDTH,
        }),
        columnType: TableColumnType.CUSTOM,
        customColumnConfig: {dataType: CustomColumnDataType.FLOAT},
      } satisfies CustomStructuredTableColumn,
      makeStructuredTableColumn({
        columnType: TableColumnType.STRING,
        name: 'Description',
        columnGroup: StructuredTableColumnGroups.REACTION_CONDITION_DESCRIPTION,
        isReadOnly: true,
      }),
      makeStructuredTableColumn({
        columnType: TableColumnType.STRING,
        name: 'Comment',
        columnGroup: StructuredTableColumnGroups.REACTION_CONDITION_COMMENT,
      }),
    ];
  }

  override injectFrontEndMetadata({column}: {column: StructuredTableColumn}): StructuredTableColumn {
    if (column.columnGroup !== StructuredTableColumnGroups.REACTION_CONDITION_UNIT) {
      return column;
    }
    return {
      ...column,
      frontEndMetadata: {
        rowScopedUnitTypeFilter: {
          sourceColumnGroup: StructuredTableColumnGroups.REACTION_CONDITION_PARAMETER,
          type: RowScopedUnitTypeFilterType.REACTION_PARAMETER_UNIT_TYPE,
        },
      },
    };
  }

  /**
   * The built-in columns drive the table's validation and Description, so they must not be user-configurable (which
   * would allow deleting them). They are identified by their column group; user-added custom columns have no column
   * group, so they defer to the default behavior and remain deletable.
   */
  override isColumnUserConfigurable(
    structuredTable: ReactionConditionsStructuredTable,
    column: StructuredTableColumn | null
  ): boolean {
    const columnGroup = column?.columnGroup;
    if (
      columnGroup != null &&
      REACTION_CONDITION_COLUMN_GROUPS.has(columnGroup as StructuredTableColumnGroups)
    ) {
      return false;
    }
    return super.isColumnUserConfigurable(structuredTable, column);
  }

  /**
   * Each row is one independent condition, and the required-cell validation reads each row's own cells, so cells
   * cannot be merged across rows.
   */
  override allowMergedCellRangeInColumn(): boolean {
    return false;
  }

  private getColumnIndex(
    tableView: EvalTableView<
      ReactionConditionsStructuredTableMetadata,
      ReactionConditionsStructuredTableRowMetadata
    >,
    columnGroup: StructuredTableColumnGroups
  ): number {
    const columnIndex = tableView.columns.findIndex((tableColumn) => tableColumn.columnGroup === columnGroup);
    assert(columnIndex !== -1, `${columnGroup} column not found`);
    return columnIndex;
  }

  override getTableSpecificCustomEvalFn(
    tableView: EvalTableView<
      ReactionConditionsStructuredTableMetadata,
      ReactionConditionsStructuredTableRowMetadata
    >,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<{
    tableMetadata: ReactionConditionsStructuredTableMetadata;
    rowMetadata: ReactionConditionsStructuredTableRowMetadata;
  }> | null {
    const {column} = this.getRowAndColumnForCellIndex(tableView, cellIndex);
    switch (column.columnGroup) {
      case StructuredTableColumnGroups.REACTION_CONDITION_DESCRIPTION:
        return {customEvalFn: libraryParameterDescriptionCustomEval, customEvalType: CustomEvalType.DEFAULT};
      case StructuredTableColumnGroups.REACTION_CONDITION_PARAMETER:
        return {customEvalFn: parameterColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
      case StructuredTableColumnGroups.REACTION_CONDITION_UNIT:
        return {customEvalFn: unitColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
      case StructuredTableColumnGroups.REACTION_CONDITION_VALUE:
        return {customEvalFn: valueColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
      default:
        return null;
    }
  }

  override getTableSpecificCustomEvalArgs(
    tableView: EvalTableView<
      ReactionConditionsStructuredTableMetadata,
      ReactionConditionsStructuredTableRowMetadata
    >,
    cellIndex: CellIndex,
    {toSpreadsheetIndex}: GetCustomEvalArgsOptions
  ): Array<string> {
    const {column} = this.getRowAndColumnForCellIndex(tableView, cellIndex);
    const parameterReference = (): string => {
      const parameterColumnIndex = this.getColumnIndex(
        tableView,
        StructuredTableColumnGroups.REACTION_CONDITION_PARAMETER
      );
      return `=${toSpreadsheetIndex({row: cellIndex.row, column: parameterColumnIndex})}`;
    };
    const isFilled = (...columnGroups: Array<StructuredTableColumnGroups>): string =>
      toSpreadsheetBooleanValue(
        columnGroups.some((columnGroup) => {
          const columnIndex = this.getColumnIndex(tableView, columnGroup);
          return isCellFilled(tableView.rows[cellIndex.row].cells[columnIndex]);
        })
      );
    switch (column.columnGroup) {
      case StructuredTableColumnGroups.REACTION_CONDITION_DESCRIPTION:
        return [parameterReference()];
      case StructuredTableColumnGroups.REACTION_CONDITION_PARAMETER:
        return [
          isFilled(
            StructuredTableColumnGroups.REACTION_CONDITION_UNIT,
            StructuredTableColumnGroups.REACTION_CONDITION_VALUE
          ),
        ];
      case StructuredTableColumnGroups.REACTION_CONDITION_UNIT:
        return [parameterReference(), isFilled(StructuredTableColumnGroups.REACTION_CONDITION_VALUE)];
      case StructuredTableColumnGroups.REACTION_CONDITION_VALUE:
        return [
          isFilled(
            StructuredTableColumnGroups.REACTION_CONDITION_PARAMETER,
            StructuredTableColumnGroups.REACTION_CONDITION_UNIT
          ),
        ];
      default:
        return [];
    }
  }
}
